import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Scale, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

export default function Reconciliation() {
  const [accounts, setAccounts] = useState([]);
  const [accId, setAccId] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/cash-accounts").then((r) => { setAccounts(r.data); if (r.data[0]) setAccId(r.data[0].id); }).catch(() => {}); }, []);
  const load = () => { if (!accId) return; api.get("/accounting/reconciliation", { params: { account_id: accId } }).then((r) => setData(r.data)); };
  useEffect(() => { setData(null); load(); }, [accId]);

  const toggle = async (t) => {
    try { await api.post(`/accounting/reconciliation/${t.id}/toggle`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Rapprochement bancaire" subtitle="Pointez les opérations figurant sur votre relevé bancaire">
        <Select value={accId} onValueChange={setAccId}>
          <SelectTrigger className="w-64" data-testid="recon-account"><SelectValue placeholder="Compte" /></SelectTrigger>
          <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.nom}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      {!data ? <Loader /> : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="rounded-xl border bg-white p-4" data-testid="recon-solde-comptable"><p className="text-[11px] uppercase font-mono text-slate-400">Solde comptable</p><p className="font-mono font-bold text-lg mt-1">{fmtMoney(data.solde_comptable)}</p></div>
            <div className="rounded-xl border bg-white p-4" data-testid="recon-solde-rapproche"><p className="text-[11px] uppercase font-mono text-slate-400">Solde rapproché</p><p className="font-mono font-bold text-lg mt-1 text-brand-700">{fmtMoney(data.solde_rapproche)}</p></div>
            <div className="rounded-xl border bg-white p-4" data-testid="recon-ecart"><p className="text-[11px] uppercase font-mono text-slate-400">Écart</p><p className={`font-mono font-bold text-lg mt-1 ${Math.abs(data.ecart) < 0.01 ? "text-emerald-600" : "text-amber-600"}`}>{fmtMoney(data.ecart)}</p></div>
          </div>

          {(data.transactions || []).length === 0 ? <EmptyState icon={Scale} title="Aucune opération" subtitle="Ce compte n'a pas encore d'opération de trésorerie." /> : (
            <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>Pointé</TableHead><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Motif</TableHead><TableHead className="text-right">Montant</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.transactions.map((t) => {
                    const sortie = (t.type || "").toLowerCase().includes("sortie");
                    return (
                      <TableRow key={t.id} data-testid={`recon-tx-${t.id}`} className={t.rapproche ? "bg-emerald-50/40" : "hover:bg-slate-50"}>
                        <TableCell><button onClick={() => toggle(t)} data-testid={`recon-toggle-${t.id}`}>{t.rapproche ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Circle className="h-5 w-5 text-slate-300" />}</button></TableCell>
                        <TableCell className="font-mono text-xs">{fmtDate(t.date)}</TableCell>
                        <TableCell><span className={`rounded px-2 py-0.5 text-xs ${sortie ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>{t.type}</span></TableCell>
                        <TableCell className="text-sm">{t.motif || "—"}</TableCell>
                        <TableCell className={`text-right font-mono text-xs ${sortie ? "text-rose-600" : "text-emerald-600"}`}>{sortie ? "-" : "+"}{fmtMoney(t.montant)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div></div>
          )}
        </>
      )}
    </div>
  );
}
